import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Shield, Plus, Search, Edit2, Trash2, Key, RefreshCw, 
  Copy, Check, X, AlertTriangle, Clock, ChevronDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_COLORS = {
  demo: 'bg-amber-100 text-amber-800',
  basico: 'bg-blue-100 text-blue-800',
  profissional: 'bg-indigo-100 text-indigo-800',
  premium: 'bg-emerald-100 text-emerald-800'
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  demo: 'bg-amber-100 text-amber-800',
  suspended: 'bg-red-100 text-red-800',
  expired: 'bg-slate-100 text-slate-600'
};

const STATUS_LABELS = {
  active: 'Ativa',
  demo: 'Demo',
  suspended: 'Suspensa',
  expired: 'Expirada'
};

const FEATURE_LABELS = {
  relatorios: 'Relatórios Básicos',
  balanco_geral: 'Balanço Geral Operacional',
  whatsapp: 'Notificações WhatsApp',
  bloqueio_vagas: 'Bloqueio de Vagas',
  backup_automatico: 'Backup Automático',
  logo_customizavel: 'Logo Customizável',
  on_premise: 'Instalação On-Premise',
  suporte_email: 'Suporte por E-mail',
  suporte_prioritario: 'Suporte Prioritário',
  suporte_dedicado: 'Suporte Dedicado + SLA',
  treinamento: 'Treinamento da Equipe'
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR');
};

const formatCNPJ = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0,2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8)}`;
  return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}`;
};

export default function LicensesPage() {
  const { user } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [expandedLicense, setExpandedLicense] = useState(null);

  const [form, setForm] = useState({
    cnpj: '', client_name: '', plan: 'basico', billing_type: 'monthly',
    price_monthly: '', price_annual: '', max_users: '', max_units: '',
    max_patients: '', max_appointments_month: '', expiration_date: '', notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [licRes, planRes] = await Promise.all([
        axios.get(`${API}/licenses`),
        axios.get(`${API}/licenses/plans`)
      ]);
      setLicenses(licRes.data);
      setPlans(planRes.data);
    } catch (err) {
      toast.error('Erro ao carregar licencas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePlanChange = (planKey) => {
    const plan = plans[planKey];
    if (!plan) return;
    setForm(prev => ({
      ...prev,
      plan: planKey,
      price_monthly: plan.price_monthly || '',
      price_annual: plan.price_annual || '',
      max_users: plan.max_users ?? '',
      max_units: plan.max_units ?? '',
      max_patients: plan.max_patients ?? '',
      max_appointments_month: plan.max_appointments_month ?? '',
      expiration_date: planKey === 'demo' 
        ? new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]
        : prev.expiration_date
    }));
  };

  const openCreate = () => {
    setEditingLicense(null);
    const defaultPlan = plans['basico'] || {};
    setForm({
      cnpj: '', client_name: '', plan: 'basico', billing_type: 'monthly',
      price_monthly: defaultPlan.price_monthly || 800,
      price_annual: defaultPlan.price_annual || 8500,
      max_users: defaultPlan.max_users ?? '',
      max_units: defaultPlan.max_units ?? '',
      max_patients: defaultPlan.max_patients ?? '',
      max_appointments_month: defaultPlan.max_appointments_month ?? '',
      expiration_date: '', notes: ''
    });
    setShowModal(true);
  };

  const openEdit = (lic) => {
    setEditingLicense(lic);
    setForm({
      cnpj: lic.cnpj || '', client_name: lic.client_name || '', plan: lic.plan || 'basico',
      billing_type: lic.billing_type || 'monthly',
      price_monthly: lic.price_monthly ?? '', price_annual: lic.price_annual ?? '',
      max_users: lic.max_users ?? '', max_units: lic.max_units ?? '',
      max_patients: lic.max_patients ?? '', max_appointments_month: lic.max_appointments_month ?? '',
      expiration_date: lic.expiration_date ? lic.expiration_date.split('T')[0] : '',
      notes: lic.notes || '', status: lic.status || 'active'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price_monthly: form.price_monthly === '' ? null : Number(form.price_monthly),
        price_annual: form.price_annual === '' ? null : Number(form.price_annual),
        max_users: form.max_users === '' ? null : Number(form.max_users),
        max_units: form.max_units === '' ? null : Number(form.max_units),
        max_patients: form.max_patients === '' ? null : Number(form.max_patients),
        max_appointments_month: form.max_appointments_month === '' ? null : Number(form.max_appointments_month),
        expiration_date: form.expiration_date ? `${form.expiration_date}T23:59:59` : null
      };

      if (editingLicense) {
        await axios.put(`${API}/licenses/${editingLicense.id}`, payload);
        toast.success('Licenca atualizada com sucesso!');
      } else {
        await axios.post(`${API}/licenses`, payload);
        toast.success('Licenca criada com sucesso!');
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar licenca');
    }
  };

  const handleDelete = async (lic) => {
    if (!window.confirm(`Remover licenca de ${lic.client_name}?`)) return;
    try {
      await axios.delete(`${API}/licenses/${lic.id}`);
      toast.success('Licenca removida');
      fetchData();
    } catch (err) {
      toast.error('Erro ao remover licenca');
    }
  };

  const handleSuspend = async (lic) => {
    const newStatus = lic.status === 'suspended' ? 'active' : 'suspended';
    try {
      await axios.put(`${API}/licenses/${lic.id}`, { status: newStatus });
      toast.success(newStatus === 'suspended' ? 'Licenca suspensa' : 'Licenca reativada');
      fetchData();
    } catch (err) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleRegenerateKey = async (lic) => {
    try {
      const res = await axios.post(`${API}/licenses/${lic.id}/regenerate-key`);
      toast.success(`Nova chave: ${res.data.activation_key}`);
      fetchData();
    } catch (err) {
      toast.error('Erro ao regenerar chave');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const filtered = licenses.filter(l =>
    l.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.cnpj?.includes(search) ||
    l.plan?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: licenses.length,
    active: licenses.filter(l => l.status === 'active').length,
    demo: licenses.filter(l => l.status === 'demo').length,
    expiring: licenses.filter(l => l.days_until_expired !== undefined && l.days_until_expired <= 30 && l.days_until_expired > 0).length,
    expired: licenses.filter(l => l.status === 'expired').length
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="licenses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-teal-700" />
            Licenciamento
          </h1>
          <p className="text-slate-500 mt-1">Gerencie licencas dos clientes por CNPJ</p>
        </div>
        <Button onClick={openCreate} className="bg-teal-700 hover:bg-teal-800" data-testid="create-license-btn">
          <Plus className="h-4 w-4 mr-2" /> Nova Licenca
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats.total, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Ativas', value: stats.active, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Demo', value: stats.demo, color: 'text-amber-700', bg: 'bg-amber-50' },
          { label: 'Expirando', value: stats.expiring, color: 'text-orange-700', bg: 'bg-orange-50' },
          { label: 'Expiradas', value: stats.expired, color: 'text-red-700', bg: 'bg-red-50' },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 border`}>
            <p className="text-sm text-slate-500">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Buscar por nome, CNPJ ou plano..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="license-search"
        />
      </div>

      {/* Licenses List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border">
            <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nenhuma licenca encontrada</p>
          </div>
        ) : filtered.map((lic) => (
          <div key={lic.id} className="bg-white rounded-xl border hover:shadow-md transition-shadow" data-testid={`license-card-${lic.id}`}>
            {/* Main Row */}
            <div className="p-5 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedLicense(expandedLicense === lic.id ? null : lic.id)}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="font-semibold text-slate-900 truncate">{lic.client_name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[lic.plan] || 'bg-slate-100'}`}>
                    {lic.plan_label || lic.plan}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[lic.status] || 'bg-slate-100'}`}>
                    {STATUS_LABELS[lic.status] || lic.status}
                  </span>
                  {lic.in_grace_period && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 font-medium flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Carencia
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>CNPJ: {lic.cnpj}</span>
                  <span>Expira: {formatDate(lic.expiration_date)}</span>
                  {lic.days_until_expired !== undefined && (
                    <span className={lic.days_until_expired <= 30 ? 'text-orange-600 font-medium' : ''}>
                      <Clock className="h-3 w-3 inline mr-1" />
                      {lic.days_until_expired} dias restantes
                    </span>
                  )}
                  <span>{formatCurrency(lic.billing_type === 'annual' ? lic.price_annual : lic.price_monthly)}/{lic.billing_type === 'annual' ? 'ano' : 'mes'}</span>
                </div>
              </div>
              <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${expandedLicense === lic.id ? 'rotate-180' : ''}`} />
            </div>

            {/* Expanded Details */}
            {expandedLicense === lic.id && (
              <div className="border-t px-5 py-4 bg-slate-50/50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-slate-500">Chave de Ativação</p>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono text-teal-700 bg-teal-50 px-2 py-1 rounded">{lic.activation_key}</code>
                      <button onClick={(e) => { e.stopPropagation(); copyToClipboard(lic.activation_key, lic.id); }} className="text-slate-400 hover:text-teal-700">
                        {copiedKey === lic.id ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Usuários</p>
                    <p className="text-sm font-medium">{lic.max_users ?? 'Ilimitado'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Unidades</p>
                    <p className="text-sm font-medium">{lic.max_units ?? 'Ilimitado'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Pacientes</p>
                    <p className="text-sm font-medium">{lic.max_patients ? lic.max_patients.toLocaleString('pt-BR') : 'Ilimitado'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Agendamentos/mes</p>
                    <p className="text-sm font-medium">{lic.max_appointments_month ? lic.max_appointments_month.toLocaleString('pt-BR') : 'Ilimitado'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Valor Mensal</p>
                    <p className="text-sm font-medium">{formatCurrency(lic.price_monthly)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Valor Anual</p>
                    <p className="text-sm font-medium">{formatCurrency(lic.price_annual)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Inicio</p>
                    <p className="text-sm font-medium">{formatDate(lic.start_date)}</p>
                  </div>
                </div>
                {lic.notes && (
                  <div className="mb-4">
                    <p className="text-xs text-slate-500">Observações</p>
                    <p className="text-sm">{lic.notes}</p>
                  </div>
                )}
                {lic.features && (
                  <div className="mb-4">
                    <p className="text-xs text-slate-500 mb-2">Funcionalidades do Plano</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                      {Object.entries(lic.features).map(([key, enabled]) => (
                        <div key={key} className="flex items-center gap-1.5">
                          {enabled 
                            ? <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0" /> 
                            : <X className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />
                          }
                          <span className={`text-xs ${enabled ? 'text-slate-700' : 'text-slate-400'}`}>
                            {FEATURE_LABELS[key] || key}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openEdit(lic); }} data-testid={`edit-license-${lic.id}`}>
                    <Edit2 className="h-3.5 w-3.5 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleRegenerateKey(lic); }} data-testid={`regen-key-${lic.id}`}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Nova Chave
                  </Button>
                  <Button size="sm" variant="outline" 
                    onClick={(e) => { e.stopPropagation(); handleSuspend(lic); }}
                    className={lic.status === 'suspended' ? 'text-green-700 hover:text-green-800' : 'text-amber-700 hover:text-amber-800'}
                  >
                    {lic.status === 'suspended' ? <><Check className="h-3.5 w-3.5 mr-1" /> Reativar</> : <><AlertTriangle className="h-3.5 w-3.5 mr-1" /> Suspender</>}
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={(e) => { e.stopPropagation(); handleDelete(lic); }} data-testid={`delete-license-${lic.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Remover
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h2 className="text-lg font-semibold text-slate-900">
                {editingLicense ? 'Editar Licenca' : 'Nova Licenca'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">CNPJ</label>
                  <Input
                    value={form.cnpj}
                    onChange={(e) => setForm({ ...form, cnpj: formatCNPJ(e.target.value) })}
                    placeholder="00.000.000/0000-00"
                    required
                    disabled={!!editingLicense}
                    data-testid="license-cnpj-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Nome do Cliente</label>
                  <Input
                    value={form.client_name}
                    onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    placeholder="Secretaria de Saude - Cidade"
                    required
                    data-testid="license-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Plano</label>
                  <select
                    value={form.plan}
                    onChange={(e) => handlePlanChange(e.target.value)}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    data-testid="license-plan-select"
                  >
                    {Object.entries(plans).map(([key, p]) => (
                      <option key={key} value={key}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Cobranca</label>
                  <select
                    value={form.billing_type}
                    onChange={(e) => setForm({ ...form, billing_type: e.target.value })}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    data-testid="license-billing-select"
                  >
                    <option value="monthly">Mensal</option>
                    <option value="annual">Anual</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Expiração</label>
                  <Input
                    type="date"
                    value={form.expiration_date}
                    onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
                    required
                    data-testid="license-expiration-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Valor Mensal (R$)</label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.price_monthly}
                    onChange={(e) => setForm({ ...form, price_monthly: e.target.value })}
                    placeholder="Conforme plano"
                    data-testid="license-price-monthly"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Valor Anual (R$)</label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.price_annual}
                    onChange={(e) => setForm({ ...form, price_annual: e.target.value })}
                    placeholder="Conforme plano"
                    data-testid="license-price-annual"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Máx. Usuários</label>
                  <Input
                    type="number" min="0"
                    value={form.max_users}
                    onChange={(e) => setForm({ ...form, max_users: e.target.value })}
                    placeholder="Ilimitado"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Max Unidades</label>
                  <Input
                    type="number" min="0"
                    value={form.max_units}
                    onChange={(e) => setForm({ ...form, max_units: e.target.value })}
                    placeholder="Ilimitado"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Max Pacientes</label>
                  <Input
                    type="number" min="0"
                    value={form.max_patients}
                    onChange={(e) => setForm({ ...form, max_patients: e.target.value })}
                    placeholder="Ilimitado"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Agend./mes</label>
                  <Input
                    type="number" min="0"
                    value={form.max_appointments_month}
                    onChange={(e) => setForm({ ...form, max_appointments_month: e.target.value })}
                    placeholder="Ilimitado"
                  />
                </div>
              </div>

              {editingLicense && (
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="active">Ativa</option>
                    <option value="demo">Demo</option>
                    <option value="suspended">Suspensa</option>
                    <option value="expired">Expirada</option>
                  </select>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Observações</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm min-h-[60px] resize-y"
                  placeholder="Notas sobre o contrato, negociação..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit" className="bg-teal-700 hover:bg-teal-800" data-testid="license-submit-btn">
                  {editingLicense ? 'Salvar Alteracoes' : 'Criar Licenca'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
